<template>
  <div class="py-7 container-fluid">
    <div class="card w-70">
      <div class="card-header pb-0">
      </div>
      <div class="card-body px-5 pt-5 pb-2">  
        <div class="markdown-body" v-html="renderedMarkdown" />
      </div>
    </div>
  </div>
</template>

<script setup>
import markdownit from 'markdown-it'
import { ref } from 'vue'


const md = markdownit({
  breaks: true
})
const rawMarkdown = `



## ✅ Tips útiles

#### 💡🔌 Uso de la energía

Les recordamos que, luego de utilizar sus entornos virtuales en el sitio de DevOps, es importante **apagarlos correctamente**.  
Esto ayuda a evitar el consumo innecesario de energía y contribuye a un uso más eficiente de los recursos.

Además, informamos que hemos implementado un sistema de apagado automático por inactividad, que apaga los entornos que superan un umbral de tiempo sin uso.
Esta medida complementa las buenas prácticas de uso, optimiza los recursos disponibles y mejora la sustentabilidad de la plataforma.

---


#### 💡📂 Uso de Git dentro del stack

Dentro del stack tendrás la posibilidad de clonar repositorios y realizar todas las operaciones de Git habituales (commit, push, pull, merge, etc.).

Para interactuar con los proyectos de GitLab, deberás:

1 - Generar un par  de llaves SSH

**ssh-keygen -t ed25519 -C "tu-email@ejemplo.com"**

2 - Agregar la clave pública en GitLab:

**cat ~/.ssh/id_ed25519.pub**

Inicia sesión en GitLab.

Ve a ***User Settings ➔ SSH Keys ➔ Add SSH Key.***

Pega la clave copiada, define una expiración si desea, y guarda.

Con esto podrás autenticarte y trabajar con Git en el stack sin necesidad de usuario y contraseña en cada operación.

---


#### 💡🚀 Visor de métricas de estado

Es posible visualizar el comportamiento del stack para determinar el uso de CPU, Memoria RAM y Disco.
En las opciones del stack, es posible acceder mediante el link ***"View metrics"***

---

#### 💡🔓 Credenciales de acceso

Las credenciales de acceso se renuevan automáticamente por seguridad cada vez que se detiene y reinicia el stack.  
Pueden encontrar los datos actualizados en la opción **"Credentials"** del sitio.

---

#### 💡📌 Portapapeles

Al conectarse al stack, el navegador puede solicitar permisos para acceder al portapapeles.  
Es importante **aceptar esta opción** para habilitar copiar y pegar entre su equipo local y el stack.  
Si no se otorgan estos permisos, **no funcionará** el copiar/pegar.

---

#### 💡💻 Comunicación entre stacks

El sistema permite acceder a servicios entre stacks de distintos proyectos.  
Cada stack tiene:
- Una IP única
- Un dominio único (se compone del nombre del stack + el nombre del proyecto + el sufijo svc.cluster.local.  
  **Ejemplo: juan-perez-stacks.proyecto1.svc.cluster.local**)
- Un rango de puertos visible **28080-28100**

📝 ***Ejemplo:***

Si un stack con el nombre juan-perez publica una app en el puerto 28081 en el proyecto1, se puede acceder desde otro stack en otro proyecto ingresando a  
**http://juan-perez-stacks.proyecto1.svc.cluster.local:28081**

---

#### 💡⌨️ Idioma del teclado

El stack utiliza por defecto el idioma del teclado del equipo anfitrión. Se recomienda tener configurado en el equipo anfitrión el idioma inglés y español.
   
**Nota importante**: Si al cambiar el idioma en el equipo anfitrión no se propaga en el stack, presione F5 para recargar la página. Esto hará que se utilice en el stack el idioma seleccionado en el anfitrión.

---

#### 💡📄 Crear un nuevo stack a partir de uno existente (Templates)
Es posible crear un nuevo stack a partir de uno ya existente. Si dentro de un proyecto hay templates disponibles, se puede solicitar un nuevo stack usando la opción "Stack Request" y seleccionando "From template".

En caso de que el proyecto no cuente con templates, pueden solicitar la creación de uno escribiendo a Infraestructura al correo ***soporte@sofis.lat***.

📝 ***Ejemplo de solicitud:***

Solicito la creación de un template a partir del stack ***stack1*** en el proyecto ***Proyecto1***.

---

#### 💡💻 Instalación de programas o aplicaciones adicionales

Si bien es posible utilizar \`apt install\` para instalar paquetes en el stack, estos se instalan de forma temporal. Una vez reiniciado el stack volverá al estado original.  
Utilice \`docker\` si desea instalar aplicaciones externas.

📝 ***Ejemplo:***

Instalar nginx:

\`\`\`bash
docker run nginx
\`\`\`

---

#### 💡🔐 Uso de VPN dentro de los stacks

Los stacks tienen incorporados los paquetes \`openfortivpn\`, \`openconnect\` y \`openvpn\`. Puede conectarse a VPN de terceros dentro del stack utilizando estos clientes.

**Nota importante**: El uso de VPN dentro de los stacks podría afectar o disminuir el rendimiento. Las conexiones VPN pueden forzar el uso de un default gateway o incluso generar conflictos con los rangos de IP del stack.

---

#### 💡⚙️ Modificar tecnologías del stack

Recuerde que puede modificar las tecnologías del stack. Esta opción se encuentra disponible en la sección ***Configure***.  
Podrá seleccionar cualquiera de las tecnologías disponibles en el sitio.

📝 ***Ejemplo:***

- backend-visualcode-python3.11-v1.0.0  
- backend-visualcode-python3.13-v1.0.0  
- frontend-visualcode-nodejs16-v1.0.0  
- frontend-visualcode-nodejs18-v1.0.0   

**Nota importante**: Al seleccionar una nueva tecnología, el stack se reiniciará.

---

#### 🖥️🚀 Ejecución automática de scripts al iniciar el stack

El sistema permite ejecutar scripts automáticamente al inicio del stack. Para ello, se debe crear un archivo con extensión \`.sh\` en el directorio \`/home/developer/autorun\`.

📝 ***Ejemplo:***

Si deseas agregar entradas al archivo \`/etc/hosts\` al iniciar el stack, crea un archivo llamado \`hosts.sh\` en el directorio \`/home/developer/autorun\` con el siguiente contenido:

\`\`\`bash
echo "127.0.0.1 mi-dominio.local" | sudo tee -a /etc/hosts  
echo "192.168.1.3 prueba.com.uy" | sudo tee -a /etc/hosts  
echo "10.10.50.10 dominio.ejemplo.com" | sudo tee -a /etc/hosts
\`\`\`

**Nota importante**: Al finalizar su ejecución, el sistema generará automáticamente un archivo con la misma ruta y nombre, pero con extensión \`.log\`, donde se registrará la salida generada por el script.

---

## 🔧 Problemas comunes

#### 💡🔧 El teclado dejó de funcionar en Visual Studio Code

Este problema puede suceder en algunos anfitriones cuando se sale del escritorio virtual y se vuelve a ingresar. Para solucionarlo, recargue la página con F5.

---

#### 💡🔧 El idioma del teclado no coincide con el anfitrión

Recargue la página para que se actualice la configuración del teclado en el stack de acuerdo con la seleccionada en el anfitrión.

---

#### 💡🔧 Lentitud o latencia en el stack

Para el acceso al stack no es necesario utilizar conexiones VPN.  
No utilice conexiones VPN mientras usa el stack, ya que estas pueden reducir la performance de red y afectar el comportamiento del entorno.

---

## 📞🧑‍💻📧 Ayuda

No dude en contactar a ***soporte@sofis.lat*** ante cualquier comentario, duda o problema.

---
`

const renderedMarkdown = ref(md.render(rawMarkdown))
</script>